/**
 * ColorPicker Module
 * Handles color selection with spectrum, palette, and hex input
 */

export class ColorPicker {
    activeColorPicker: HTMLElement | null;
    activePreview: HTMLElement | null;
    onColorChange: ((color: string, preview: HTMLElement) => void) | null;
    outsideClickHandler: ((e: MouseEvent) => void) | null;
    escapeKeyHandler: ((e: KeyboardEvent) => void) | null;

    constructor() {
        this.activeColorPicker = null;
        this.activePreview = null;
        this.onColorChange = null;
        this.outsideClickHandler = null;
        this.escapeKeyHandler = null;
    }

    /**
     * Set callback function for color changes
     * @param callback - Function to call when color changes
     */
    setOnColorChange(callback: (color: string, preview: HTMLElement) => void): void {
        this.onColorChange = callback;
    }

    /**
     * Open color picker near the given preview element
     * @param preview - The color preview element that was clicked
     */
    open(preview: HTMLElement): void {
        // Close any existing color picker
        this.close();
        
        // Get current color
        const currentColor = window.ColorUtils?.rgb2hex(preview.style.backgroundColor) || '#000000';
        
        // Create color picker container
        const colorPicker = this._createColorPickerElement();
        
        // Position near the preview element
        this._positionColorPicker(colorPicker, preview);
        
        // Initialize color picker functionality
        this._initializeColorSpectrum(colorPicker, currentColor, preview);
        
        // Store reference for cleanup
        this.activeColorPicker = colorPicker;
        this.activePreview = preview;
    }
    
    /**
     * Close the active color picker
     */
    close(): void {
        if (this.activeColorPicker) {
            // Remove the backdrop (parent of color picker)
            const backdrop = this.activeColorPicker.parentElement;
            if (backdrop && backdrop.classList.contains('color-picker-backdrop')) {
                backdrop.remove();
            } else {
                // Fallback if structure changes
                this.activeColorPicker.remove();
            }
            this.activeColorPicker = null;
            this.activePreview = null;
        }
        
        // Clean up event listeners
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        if (this.escapeKeyHandler) {
            document.removeEventListener('keydown', this.escapeKeyHandler);
            this.escapeKeyHandler = null;
        }
    }

    /**
     * Apply color to preview element and trigger callback
     * @param color - Hex color value
     * @param preview - Preview element to update
     */
    applyColor(color: string, preview: HTMLElement): void {
        preview.style.backgroundColor = color;
        
        // Update corresponding text input
        if (preview.classList.contains('bg-preview')) {
            const bgInput = document.getElementById('backgroundColorText') as HTMLInputElement;
            if (bgInput) bgInput.value = color;
        } else if (preview.classList.contains('text-preview')) {
            const textInput = document.getElementById('textColorText') as HTMLInputElement;
            if (textInput) textInput.value = color;
        }
        
        // Trigger callback if set
        if (this.onColorChange) {
            this.onColorChange(color, preview);
        }
    }

    /**
     * Create the color picker DOM element
     * @returns Color picker element
     * @private
     */
    _createColorPickerElement(): HTMLElement {
        // Create backdrop
        const backdrop = document.createElement('div');
        backdrop.className = 'color-picker-backdrop';
        
        // Create color picker
        const colorPicker = document.createElement('div');
        colorPicker.className = 'custom-color-picker';
        colorPicker.innerHTML = `
            <div class="color-picker-header">
                <span class="color-picker-title">Choose Color</span>
                <button class="color-picker-close" aria-label="Close color picker">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>
            <div class="color-picker-content">
                <div class="color-spectrum-container">
                    <canvas class="color-spectrum" width="180" height="120"></canvas>
                    <div class="color-picker-cursor"></div>
                </div>
                <div class="color-hue-slider">
                    <input type="range" class="hue-slider" min="0" max="360" value="0">
                    <div class="hue-slider-track"></div>
                </div>
                <div class="color-palette">
                    <div class="palette-colors">
                        <button class="palette-color" data-color="#3B82F6" style="background: #3B82F6"></button>
                        <button class="palette-color" data-color="#EF4444" style="background: #EF4444"></button>
                        <button class="palette-color" data-color="#10B981" style="background: #10B981"></button>
                        <button class="palette-color" data-color="#F59E0B" style="background: #F59E0B"></button>
                        <button class="palette-color" data-color="#8B5CF6" style="background: #8B5CF6"></button>
                        <button class="palette-color" data-color="#EC4899" style="background: #EC4899"></button>
                        <button class="palette-color" data-color="#06B6D4" style="background: #06B6D4"></button>
                        <button class="palette-color" data-color="#84CC16" style="background: #84CC16"></button>
                        <button class="palette-color" data-color="#F97316" style="background: #F97316"></button>
                        <button class="palette-color" data-color="#6366F1" style="background: #6366F1"></button>
                        <button class="palette-color" data-color="#000000" style="background: #000000"></button>
                        <button class="palette-color" data-color="#FFFFFF" style="background: #FFFFFF; border: 1px solid #E5E7EB"></button>
                    </div>
                </div>
                <div class="color-input-section">
                    <div class="current-color-display"></div>
                    <input type="text" class="color-hex-input" placeholder="#000000" maxlength="7">
                </div>
            </div>
        `;
        
        // Append color picker to backdrop
        backdrop.appendChild(colorPicker);
        
        // Add backdrop click handler to close
        backdrop.addEventListener('click', (e) => {
            if (e.target === backdrop) {
                this.close();
            }
        });
        
        document.body.appendChild(backdrop);
        return colorPicker;
    }

    /**
     * Position the color picker relative to the preview element
     * Note: With backdrop overlay, positioning is now centered by CSS
     * This method is kept for backwards compatibility but does nothing
     * @param colorPicker - Color picker element
     * @param preview - Preview element
     * @private
     */
    _positionColorPicker(_colorPicker: HTMLElement, _preview: HTMLElement): void {
        // Color picker is now centered via backdrop flexbox
        // No manual positioning needed
    }

    /**
     * Initialize the color spectrum functionality
     * @param colorPicker - Color picker element
     * @param currentColor - Current hex color
     * @param preview - Preview element
     * @private
     */
    _initializeColorSpectrum(colorPicker: HTMLElement, currentColor: string, preview: HTMLElement): void {
        const canvas = colorPicker.querySelector('.color-spectrum') as HTMLCanvasElement;
        const ctx = canvas.getContext('2d');
        const cursor = colorPicker.querySelector('.color-picker-cursor') as HTMLElement;
        const hueSlider = colorPicker.querySelector('.hue-slider') as HTMLInputElement;
        const hexInput = colorPicker.querySelector('.color-hex-input') as HTMLInputElement;
        const currentColorDisplay = colorPicker.querySelector('.current-color-display') as HTMLElement;
        
        if (!ctx) return;
        
        let currentHue = 0;
        let currentSaturation = 100;
        let currentLightness = 50;
        
        // Parse current color
        if (window.ColorUtils) {
            const hsl = window.ColorUtils.hexToHsl(currentColor);
            currentHue = hsl.h;
            currentSaturation = hsl.s;
            currentLightness = hsl.l;
        }
        
        hueSlider.value = currentHue.toString();
        hexInput.value = currentColor;
        
        // Draw spectrum
        const drawSpectrum = () => {
            const imageData = ctx.createImageData(canvas.width, canvas.height);
            const data = imageData.data;
            
            for (let y = 0; y < canvas.height; y++) {
                for (let x = 0; x < canvas.width; x++) {
                    const index = (y * canvas.width + x) * 4;
                    const saturation = (x / canvas.width) * 100;
                    const lightness = 100 - (y / canvas.height) * 100;
                    
                    const rgb = window.ColorUtils?.hslToRgb(currentHue, saturation, lightness) || { r: 0, g: 0, b: 0 };
                    data[index] = rgb.r;
                    data[index + 1] = rgb.g;
                    data[index + 2] = rgb.b;
                    data[index + 3] = 255;
                }
            }
            
            ctx.putImageData(imageData, 0, 0);
        };
        
        // Update cursor position
        const updateCursor = () => {
            const x = (currentSaturation / 100) * canvas.width;
            const y = (1 - currentLightness / 100) * canvas.height;
            cursor.style.left = x + 'px';
            cursor.style.top = y + 'px';
        };
        
        // Update color
        const updateColor = () => {
            const newColor = window.ColorUtils?.hslToHex(currentHue, currentSaturation, currentLightness) || '#000000';
            hexInput.value = newColor;
            currentColorDisplay.style.backgroundColor = newColor;
            this.applyColor(newColor, preview);
        };
        
        // Initial draw
        drawSpectrum();
        updateCursor();
        currentColorDisplay.style.backgroundColor = currentColor;
        
        // Hue slider events
        hueSlider.addEventListener('input', (e) => {
            currentHue = parseInt((e.target as HTMLInputElement).value);
            drawSpectrum();
            updateColor();
        });
        
        // Spectrum click events
        canvas.addEventListener('mousedown', (e) => {
            const handleMouseMove = (e: MouseEvent) => {
                const rect = canvas.getBoundingClientRect();
                const x = Math.max(0, Math.min(canvas.width, e.clientX - rect.left));
                const y = Math.max(0, Math.min(canvas.height, e.clientY - rect.top));
                
                currentSaturation = (x / canvas.width) * 100;
                currentLightness = 100 - (y / canvas.height) * 100;
                
                updateCursor();
                updateColor();
            };
            
            const handleMouseUp = () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
            
            handleMouseMove(e);
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        });
        
        // Palette color events
        colorPicker.querySelectorAll('.palette-color').forEach(button => {
            button.addEventListener('click', () => {
                const color = (button as HTMLElement).dataset.color || '#000000';
                if (window.ColorUtils) {
                    const hsl = window.ColorUtils.hexToHsl(color);
                    currentHue = hsl.h;
                    currentSaturation = hsl.s;
                    currentLightness = hsl.l;
                }
                
                hueSlider.value = currentHue.toString();
                drawSpectrum();
                updateCursor();
                currentColorDisplay.style.backgroundColor = color;
                this.applyColor(color, preview);
                hexInput.value = color;
            });
        });
        
        // Hex input events
        hexInput.addEventListener('input', (e) => {
            const color = (e.target as HTMLInputElement).value;
            if (/^#[0-9A-Fa-f]{6}$/.test(color) && window.ColorUtils) {
                const hsl = window.ColorUtils.hexToHsl(color);
                currentHue = hsl.h;
                currentSaturation = hsl.s;
                currentLightness = hsl.l;
                
                hueSlider.value = currentHue.toString();
                drawSpectrum();
                updateCursor();
                currentColorDisplay.style.backgroundColor = color;
                this.applyColor(color, preview);
            }
        });
        
        // Close button
        colorPicker.querySelector('.color-picker-close')?.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            this.close();
        });
        
        // Close on outside click or escape key
        this.outsideClickHandler = (e: MouseEvent) => {
            if (!colorPicker.contains(e.target as Node) && !preview.contains(e.target as Node)) {
                this.close();
            }
        };
        
        this.escapeKeyHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                this.close();
            }
        };
        
        setTimeout(() => {
            if (this.outsideClickHandler) {
                document.addEventListener('click', this.outsideClickHandler);
            }
            if (this.escapeKeyHandler) {
                document.addEventListener('keydown', this.escapeKeyHandler);
            }
        }, 100);
    }
}
